import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, AlertCircle, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import type { IAccountDataResponse, IGroupData, SideConfig } from '@tool-no-hu/shared/types';
import { GameType, AccountSide } from '@tool-no-hu/shared/enums';
import { updateAccountProxyService } from '../service/account';
import { updateGroupConfigService } from '../service/group';
import { getProxiesService, checkProxyHealthService } from '../service/proxy';
import { CustomDropdown } from './CustomDropdown';
import { showToast } from '../context/ToastContext';

// ─── Types ────────────────────────────────────────────────────────────────

interface GroupConfigPopupProps {
  isOpen: boolean;
  onClose: () => void;
  group: IGroupData;
  onSaved: (updatedGroup: IGroupData) => void | Promise<void>;
}

type ProxyHealth = 'idle' | 'checking' | 'alive' | 'dead';

/** Proxy giờ là setting per-account duy nhất còn lại trong popup này — side/bet
 * config đã chuyển thành cấu hình cấp nhóm (2 ô "Side Chẵn"/"Side Lẻ" ở trên). */
interface MemberDraft {
  id: string;
  username: string;
  isRunning: boolean;
  proxy: string;
  /** Proxy lúc mở popup — chỉ gọi API cập nhật cho account thực sự đổi proxy. */
  initialProxy: string;
  proxyHealth: ProxyHealth;
}

const NO_PROXY = 'No Proxy';
const REQUIRED_MEMBER_COUNT = 4;

const GAME_OPTIONS: { value: GameType; label: string }[] = [
  { value: GameType.TX, label: 'Tài Xỉu' },
  { value: GameType.XD, label: 'Xóc Đĩa' },
  { value: GameType.TL, label: 'Tứ Linh' },
];

/**
 * Giải thích cách chia tiền của 1 side, khớp bên server (sunwin-game.service.ts):
 *   - TX (`allocateTxHedgedRound`): đánh đối — Tài = Xỉu = Chẵn = Lẻ = 1/4 tổng
 *     cược (2 side bắt buộc bằng nhau), phần mỗi tài khoản lệch ngẫu nhiên quanh
 *     mức chia đều, luôn tròn hàng chục.
 *   - XD/TL (`allocateSideBets` + `SIDE_BET_KEY_RATIOS`): tổng tiền side chia cho
 *     2 tài khoản theo TỈ LỆ SỐ DƯ, rồi mỗi tài khoản tự chia phần của MÌNH cho
 *     các cửa phụ theo tỉ lệ cố định (2:1:1 cho side lẻ).
 * Xóc Đĩa và Tứ Linh dùng chung 1 luật.
 */
const SIDE_MEANING_BY_GAME: Record<GameType, { chan: string; le: string }> = {
  [GameType.TX]: {
    chan:
      '2 tài khoản cùng đánh Tài + Chẵn, tiền side chia đôi: tổng Tài = tổng Chẵn = nửa tiền side. ' +
      'Mỗi tài khoản lệch ngẫu nhiên quanh mức chia đều (nặng Tài thì nhẹ Chẵn), mỗi số tiền tròn ' +
      'hàng chục (chỉ tiền quá bé mới lẻ hàng đơn vị). Vd side Chẵn 800: Tài 260 + 140, Chẵn 60 + 340.',
    le:
      '2 tài khoản cùng đánh Xỉu + Lẻ, tổng Xỉu = tổng Lẻ = nửa tiền side. Vì 2 side bằng nhau nên ' +
      'Tài = Xỉu = Chẵn = Lẻ = 1/4 tổng cược, ván nào cũng chỉ lỗ ~1%. Vd side Lẻ 800: Xỉu 220 + ' +
      '180, Lẻ 170 + 230.',
  },
  [GameType.XD]: {
    chan:
      'tổng tiền side chia cho 2 tài khoản theo tỉ lệ SỐ DƯ (số dư nhiều cược nhiều hơn), mỗi tài ' +
      'khoản đánh TRỌN phần của mình vào 1 cửa Chẵn. Vd side Chẵn 600: tài khoản 1 (số dư nhiều ' +
      'hơn) nhận 400 → Chẵn 400; tài khoản 2 nhận 200 → Chẵn 200.',
    le:
      'tổng tiền side chia cho 2 tài khoản theo tỉ lệ SỐ DƯ (số dư nhiều cược nhiều hơn), rồi mỗi ' +
      'tài khoản tự chia phần của mình thành Lẻ + 3 Đỏ + 3 Trắng theo tỉ lệ 2:1:1. Vd side Lẻ 600: ' +
      'tài khoản 1 (số dư nhiều hơn) nhận 400 → Lẻ 200 + 3 Đỏ 100 + 3 Trắng 100; tài khoản 2 nhận ' +
      '200 → Lẻ 100 + 3 Đỏ 50 + 3 Trắng 50.',
  },
  [GameType.TL]: {
    chan:
      'tổng tiền side chia cho 2 tài khoản theo tỉ lệ SỐ DƯ (số dư nhiều cược nhiều hơn), mỗi tài ' +
      'khoản đánh TRỌN phần của mình vào 1 cửa Chẵn. Vd side Chẵn 600: tài khoản 1 (số dư nhiều ' +
      'hơn) nhận 400 → Chẵn 400; tài khoản 2 nhận 200 → Chẵn 200.',
    le:
      'tổng tiền side chia cho 2 tài khoản theo tỉ lệ SỐ DƯ (số dư nhiều cược nhiều hơn), rồi mỗi ' +
      'tài khoản tự chia phần của mình thành Lẻ + 3 Đỏ + 3 Trắng theo tỉ lệ 2:1:1. Vd side Lẻ 600: ' +
      'tài khoản 1 (số dư nhiều hơn) nhận 400 → Lẻ 200 + 3 Đỏ 100 + 3 Trắng 100; tài khoản 2 nhận ' +
      '200 → Lẻ 100 + 3 Đỏ 50 + 3 Trắng 50.',
  },
};

const MEMBER_COLORS = ['#60a5fa', '#34d399', '#a78bfa', '#fb923c', '#f472b6', '#2dd4bf'];

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatNumberInput(val: string): string {
  const digits = val.replace(/[^\d]/g, '');
  if (!digits) return '';
  const num = parseInt(digits, 10);
  return num.toLocaleString('vi-VN');
}

function parseFormattedNumber(val: string): number {
  const digits = val.replace(/[^\d]/g, '');
  return parseInt(digits, 10) || 0;
}

function getGroupMembers(group: IGroupData): IAccountDataResponse[] {
  return ((group.members || []) as IAccountDataResponse[]).filter(
    (m): m is IAccountDataResponse => !!m && typeof m !== 'string' && !!m._id,
  );
}

/** Member chưa resolve được thành account (VD account vừa bị xoá) — giữ nguyên id để
 * PUT không vô tình xoá mất bản ghi đó khỏi nhóm. */
function getRawMemberIds(group: IGroupData): string[] {
  return ((group.members || []) as any[])
    .map((m) => {
      if (typeof m === 'string') return m;
      if (m && typeof m === 'object' && !m._id && m.accountId) return String(m.accountId);
      return null;
    })
    .filter((id): id is string => !!id);
}

function sideAmount(sideConfigs: SideConfig[] | undefined, side: AccountSide): number {
  return sideConfigs?.find((s) => s.side === side)?.amount || 0;
}

function buildDraft(member: IAccountDataResponse): MemberDraft {
  const proxy = member.proxy && member.proxy !== NO_PROXY ? member.proxy : NO_PROXY;

  return {
    id: String(member._id),
    username: member.username || '',
    isRunning: member.status === 'RUNNING',
    proxy,
    initialProxy: proxy,
    proxyHealth: 'idle',
  };
}

/**
 * Lý do không lưu được của 1 thành viên, null nếu hợp lệ.
 * Account đang chạy không đổi được proxy (form khoá) nên cũng không validate proxy —
 * nếu không user sẽ kẹt: bị chặn lưu vì một field mà chính họ không sửa được.
 */
function validateDraft(draft: MemberDraft): string | null {
  if (!draft.isRunning && draft.proxy === NO_PROXY) return 'chưa chọn proxy';
  return null;
}

// ─── Segment Tab ──────────────────────────────────────────────────────────

const SegmentTab: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  disabled?: boolean;
  onChange: (val: string) => void;
}> = ({ options, value, disabled, onChange }) => (
  <div className="inline-flex rounded-lg border border-[#252a38] bg-[#0d0f14] p-0.5">
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          className={`rounded-md px-3 py-1.5 text-[12px] font-bold transition-all ${
            active ? 'bg-[#3b82f6] text-white shadow-sm' : 'text-[#64748b] hover:text-[#e2e8f0]'
          } disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-[#64748b]`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────

export const GroupConfigPopup: React.FC<GroupConfigPopupProps> = ({
  isOpen,
  onClose,
  group,
  onSaved,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [gameType, setGameType] = useState<string>(GameType.TX);
  const [drafts, setDrafts] = useState<MemberDraft[]>([]);
  const [proxyList, setProxyList] = useState<string[]>([]);
  // Số tiền cược cấp nhóm cho mỗi side — server tự random chia 2 acc + cửa phụ mỗi
  // round, UI chỉ cần đúng 1 số VND cho Chẵn và 1 số cho Lẻ.
  const [chanAmount, setChanAmount] = useState('');
  const [leAmount, setLeAmount] = useState('');

  const anyRunning = drafts.some((d) => d.isRunning);
  // 2 side luôn cùng tiền (mọi game) — TX đánh đối chỉ khớp Tài = Xỉu = Chẵn = Lẻ
  // khi 2 side bằng nhau. Gõ ô nào ô kia theo.
  const handleSideAmount = (raw: string) => {
    const value = formatNumberInput(raw);
    setChanAmount(value);
    setLeAmount(value);
  };
  const memberCount = getGroupMembers(group).length;

  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      setLoading(true);
      const initialGame = group.gameType || GameType.TX;
      setGroupName(group.name || '');
      setGameType(initialGame);

      const chan = sideAmount(group.sideConfigs, AccountSide.CHAN);
      const le = sideAmount(group.sideConfigs, AccountSide.LE);
      setChanAmount(chan > 0 ? chan.toLocaleString('vi-VN') : '');
      setLeAmount(le > 0 ? le.toLocaleString('vi-VN') : '');

      const members = getGroupMembers(group);
      setDrafts(members.map((m) => buildDraft(m)));

      try {
        const prxList = await getProxiesService();
        setProxyList(prxList.map((p) => p.value));
      } catch (err) {
        console.error('Failed to load proxies:', err);
        showToast.error('Không tải được danh sách proxy');
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, group._id]);

  const patchDraft = (id: string, patch: Partial<MemberDraft>) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  };

  const handleProxyChange = (id: string, value: string) => {
    patchDraft(id, { proxy: value, proxyHealth: 'idle' });
  };

  const handleCheckHealth = async (draft: MemberDraft) => {
    if (draft.proxy === NO_PROXY || draft.proxyHealth === 'checking') return;
    patchDraft(draft.id, { proxyHealth: 'checking' });
    const alive = await checkProxyHealthService(draft.proxy);
    patchDraft(draft.id, { proxyHealth: alive ? 'alive' : 'dead' });
  };

  // Đổi game KHÔNG còn xoá gì nữa: sideConfigs giờ chỉ là số VND chung cho mỗi side,
  // không gắn với bet key cụ thể của game nào nên không có gì cần dọn. Chỉ ý nghĩa của
  // từng side (cửa phụ nào được cộng vào) thay đổi theo game — nêu trong hint bên dưới.
  const handleGamePick = (next: string) => {
    if (next === gameType || saving) return;
    if (anyRunning) return;
    setGameType(next as GameType);
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const blockingIssues = drafts
    .map((d) => {
      const reason = validateDraft(d);
      return reason ? `${d.username}: ${reason}` : null;
    })
    .filter((p): p is string => p !== null);

  // Cảnh báo không chặn lưu — nêu rủi ro để khách tự quyết, nhất quán với cách file
  // này luôn ưu tiên không-chặn khi có thể.
  const readinessWarnings: string[] = [];
  if (memberCount !== REQUIRED_MEMBER_COUNT) {
    readinessWarnings.push(
      `Nhóm đang có ${memberCount} tài khoản — cần đúng ${REQUIRED_MEMBER_COUNT} tài khoản để chơi.`,
    );
  }
  if (parseFormattedNumber(chanAmount) <= 0 || parseFormattedNumber(leAmount) <= 0) {
    readinessWarnings.push('Cần nhập số tiền cược lớn hơn 0 cho cả 2 side (Chẵn và Lẻ) để nhóm chạy được.');
  }

  const handleSave = async () => {
    if (saving) return;

    const trimmedName = groupName.trim();
    if (!trimmedName) {
      showToast.error('Tên nhóm không được để trống');
      return;
    }
    if (drafts.length === 0) {
      showToast.error('Nhóm chưa có tài khoản nào để cấu hình');
      return;
    }

    if (blockingIssues.length > 0) {
      showToast.error(blockingIssues.join(' · '));
      return;
    }

    if (parseFormattedNumber(chanAmount) !== parseFormattedNumber(leAmount)) {
      showToast.error('Tiền Side Chẵn phải bằng tiền Side Lẻ');
      return;
    }

    setSaving(true);
    try {
      // 1. Tự kiểm tra proxy khi submit — không bắt user bấm Check thủ công.
      //    Bỏ qua proxy đã Live trong phiên này và tài khoản đang chạy: ô proxy của nó
      //    vẫn khoá nên không đổi được, kiểm tra lại chỉ chặn việc lưu cược mới.
      const needCheck = drafts.filter((d) => !d.isRunning && d.proxyHealth !== 'alive');
      if (needCheck.length > 0) {
        const needCheckIds = new Set(needCheck.map((d) => d.id));
        setDrafts((prev) =>
          prev.map((d) => (needCheckIds.has(d.id) ? { ...d, proxyHealth: 'checking' } : d)),
        );

        const results = await Promise.all(
          needCheck.map(async (d) => ({
            id: d.id,
            username: d.username,
            alive: await checkProxyHealthService(d.proxy),
          })),
        );

        const healthById = new Map(results.map((r) => [r.id, r.alive] as const));
        setDrafts((prev) =>
          prev.map((d) =>
            healthById.has(d.id)
              ? { ...d, proxyHealth: healthById.get(d.id) ? 'alive' : 'dead' }
              : d,
          ),
        );

        const dead = results.filter((r) => !r.alive);
        if (dead.length > 0) {
          showToast.error(
            `Proxy không hoạt động: ${dead.map((d) => d.username).join(', ')}. Chọn proxy khác rồi lưu lại.`,
          );
          return;
        }
      }

      // 2. Tên + game + members + sideConfigs đi trong đúng 1 request.
      //    PUT thay nguyên mảng members nên phải gửi lại cả member không resolve được
      //    account (vừa bị xoá) — bỏ sót là mất luôn bản ghi đó khỏi nhóm.
      const sideConfigs: SideConfig[] = [
        { side: AccountSide.CHAN, amount: parseFormattedNumber(chanAmount) },
        { side: AccountSide.LE, amount: parseFormattedNumber(leAmount) },
      ];

      const updated = await updateGroupConfigService(group._id, {
        name: trimmedName,
        gameType,
        members: [...drafts.map((d) => d.id), ...getRawMemberIds(group)],
        sideConfigs,
      });

      // Service trả null khi lỗi; interceptor đã hiện message của server
      if (!updated) return;

      // 3. Proxy thuộc Account nên phải gọi API riêng — chỉ gửi account thực sự đổi
      const failedProxy: string[] = [];
      for (const draft of drafts) {
        if (draft.proxy === draft.initialProxy) continue;
        const ok = await updateAccountProxyService(draft.id, draft.proxy);
        if (!ok) failedProxy.push(draft.username);
      }

      if (failedProxy.length > 0) {
        showToast.error(
          `Đã lưu cấu hình nhóm nhưng cập nhật proxy thất bại: ${failedProxy.join(', ')}`,
        );
      }

      await onSaved(updated);
      onClose();
    } catch (err) {
      console.error('Failed to save group config:', err);
      showToast.error('Lưu cấu hình thất bại');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/60" onClick={handleClose} />

          <motion.div
            className="relative max-h-[90vh] w-full max-w-[880px] overflow-y-auto rounded-xl border border-[#252a38] bg-[#161920] shadow-2xl"
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[#252a38] px-5 py-4">
              <h3 className="text-[14px] font-bold text-[#e2e8f0]">
                Sửa nhóm: <span className="text-[#3b82f6]">{group.name}</span>
              </h3>
              <button
                onClick={handleClose}
                disabled={saving}
                className="rounded-lg p-1.5 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={28} className="animate-spin text-[#3b82f6]" />
              </div>
            ) : (
              <>
                {/* Group: tên + game */}
                <div className="space-y-4 px-5 pt-5">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
                    <div>
                      <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                        Tên nhóm
                      </label>
                      <input
                        type="text"
                        value={groupName}
                        disabled={saving}
                        onChange={(e) => setGroupName(e.target.value)}
                        placeholder="Điền tên nhóm..."
                        className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3.5 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6] disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                        Game
                      </label>
                      <SegmentTab
                        options={GAME_OPTIONS}
                        value={gameType}
                        disabled={saving || anyRunning}
                        onChange={handleGamePick}
                      />
                    </div>
                  </div>

                  {/* Cấu hình cược theo side — cấp nhóm, server tự random chia cho 2 acc
                      của mỗi side + cửa phụ mỗi round, nên chỉ cần 1 số tiền / side. */}
                  <div>
                    <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                      Cấu hình cược theo side
                      <span className="ml-1 normal-case tracking-normal text-[#94a3b8]">
                        (2 side luôn bằng nhau)
                      </span>
                    </label>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="flex items-center justify-between gap-2 rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-2.5">
                        <span className="text-[12px] font-bold text-[#7dd3fc]">Side Chẵn</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={chanAmount}
                          disabled={saving}
                          onChange={(e) => handleSideAmount(e.target.value)}
                          placeholder="0"
                          className="w-32 rounded-md border border-[#252a38] bg-[#080a10] px-2.5 py-1.5 text-right font-mono text-[12px] font-bold text-[#e2e8f0] outline-none transition-colors focus:border-[#3b82f6] disabled:cursor-not-allowed disabled:opacity-40"
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2 rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-2.5">
                        <span className="text-[12px] font-bold text-[#94a3b8]">Side Lẻ</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={leAmount}
                          disabled={saving}
                          onChange={(e) => handleSideAmount(e.target.value)}
                          placeholder="0"
                          className="w-32 rounded-md border border-[#252a38] bg-[#080a10] px-2.5 py-1.5 text-right font-mono text-[12px] font-bold text-[#e2e8f0] outline-none transition-colors focus:border-[#3b82f6] disabled:cursor-not-allowed disabled:opacity-40"
                        />
                      </div>
                    </div>
                  </div>

                  {anyRunning ? (
                    <div className="flex items-start gap-2 rounded-lg border border-[#f59e0b]/35 bg-[#f59e0b]/10 px-3 py-2.5 text-[11px] leading-relaxed text-[#f59e0b]">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                      <span>
                        Nhóm đang chạy — sửa <b>số tiền mỗi side</b> vẫn được, thay đổi áp dụng từ{' '}
                        <b>round kế tiếp</b> (round đang chạy giữ nguyên cược cũ). Riêng <b>game</b>{' '}
                        và <b>proxy</b> phải bấm <b>Stop</b> ở màn hình Sảnh mới đổi được.
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 rounded-lg border border-[#f59e0b]/35 bg-[#f59e0b]/10 px-3 py-2.5 text-[11px] leading-relaxed text-[#f59e0b]">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                      <span>
                        <b>Side Chẵn</b> —{' '}
                        {SIDE_MEANING_BY_GAME[gameType as GameType]?.chan ??
                          SIDE_MEANING_BY_GAME[GameType.TX].chan}
                        <br />
                        <b>Side Lẻ</b> —{' '}
                        {SIDE_MEANING_BY_GAME[gameType as GameType]?.le ??
                          SIDE_MEANING_BY_GAME[GameType.TX].le}
                        <br />
                        Mỗi round, server tự random 2/4 tài khoản vào mỗi side (đổi ngẫu nhiên mỗi
                        ván) — số tiền mỗi side{' '}
                        <b>được giữ nguyên</b> khi đổi game.
                      </span>
                    </div>
                  )}

                  {readinessWarnings.length > 0 && (
                    <div className="flex items-start gap-2 rounded-lg border border-[#f59e0b]/35 bg-[#f59e0b]/10 px-3 py-2.5 text-[11px] leading-relaxed text-[#f59e0b]">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                      <div className="flex flex-col gap-1">
                        <span className="font-bold">Nhóm chưa sẵn sàng để chạy — vẫn lưu được</span>
                        {readinessWarnings.map((warning, i) => (
                          <span key={i}>• {warning}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Members — 2 cột song song, giờ chỉ còn proxy là setting per-account */}
                <div className="px-5 pt-5">
                  {drafts.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-[#252a38] py-10 text-center text-[12px] text-[#475569]">
                      Nhóm chưa có tài khoản nào. Thêm tài khoản ở trang Nhóm trước.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {drafts.map((draft, index) => {
                        // Proxy không sửa được khi đang chạy: kết nối WS đang chạy đã bind
                        // vào proxy cũ, đổi trong DB chỉ làm lệch giữa thứ hiển thị và thứ
                        // đang thực chạy.
                        const proxyLocked = saving || draft.isRunning;
                        return (
                          <div
                            key={draft.id}
                            className="flex flex-col gap-4 rounded-xl border border-[#252a38] bg-[#0d0f14] p-4"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span
                                className="truncate text-[13px] font-bold"
                                style={{ color: MEMBER_COLORS[index % MEMBER_COLORS.length] }}
                                title={draft.username}
                              >
                                {draft.username}
                              </span>
                              <span
                                className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                                  draft.isRunning
                                    ? 'border-[#f59e0b]/40 bg-[#f59e0b]/10 text-[#f59e0b]'
                                    : 'border-[#14532d] bg-[#0d1f14] text-[#22c55e]'
                                }`}
                              >
                                {draft.isRunning ? 'Đang chạy' : 'Đã dừng'}
                              </span>
                            </div>

                            <div>
                              <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                                Proxy
                                {draft.isRunning && (
                                  <span className="ml-1.5 normal-case tracking-normal text-[#f59e0b]">
                                    — khoá khi đang chạy, Stop để đổi
                                  </span>
                                )}
                              </label>
                              <div className="flex items-start gap-2">
                                <div className="min-w-0 flex-1">
                                  <CustomDropdown
                                    value={draft.proxy}
                                    onChange={(v) => handleProxyChange(draft.id, v)}
                                    disabled={proxyLocked}
                                    placeholder="-- Chọn proxy --"
                                    className="w-full"
                                    maxHeight={200}
                                    openId={`group-config-proxy-${draft.id}`}
                                    options={(() => {
                                      const opts = [
                                        { value: NO_PROXY, label: NO_PROXY },
                                        ...proxyList.map((prx) => ({ value: prx, label: prx })),
                                      ];
                                      if (
                                        draft.proxy &&
                                        draft.proxy !== NO_PROXY &&
                                        !proxyList.includes(draft.proxy)
                                      ) {
                                        opts.push({ value: draft.proxy, label: draft.proxy });
                                      }
                                      return opts;
                                    })()}
                                  />
                                </div>
                                <button
                                  onClick={() => handleCheckHealth(draft)}
                                  disabled={proxyLocked || draft.proxy === NO_PROXY || draft.proxyHealth === 'checking'}
                                  className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-2.5 text-[12px] font-bold text-[#94a3b8] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:cursor-not-allowed disabled:opacity-50"
                                  title="Kiểm tra proxy (không bắt buộc — lưu sẽ tự kiểm tra)"
                                >
                                  {draft.proxyHealth === 'checking' ? (
                                    <Loader2 size={14} className="animate-spin text-[#3b82f6]" />
                                  ) : draft.proxyHealth === 'alive' ? (
                                    <CheckCircle2 size={14} className="text-[#22c55e]" />
                                  ) : draft.proxyHealth === 'dead' ? (
                                    <XCircle size={14} className="text-[#ef4444]" />
                                  ) : (
                                    <Loader2 size={14} />
                                  )}
                                  <span className="hidden sm:inline">
                                    {draft.proxyHealth === 'checking'
                                      ? 'Đang kiểm tra...'
                                      : draft.proxyHealth === 'alive'
                                        ? 'Live'
                                        : draft.proxyHealth === 'dead'
                                          ? 'Dead'
                                          : 'Check'}
                                  </span>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Hint */}
                <div className="flex flex-col gap-1.5 px-5 pt-4">
                  <p className="flex items-center gap-1.5 text-[10px] text-[#475569]">
                    <AlertCircle size={11} />
                    Thay đổi có hiệu lực từ round tiếp theo. Proxy được kiểm tra tự động khi bấm Lưu.
                  </p>
                  {blockingIssues.length > 0 && (
                    <p className="text-[10px] font-semibold text-[#ef4444]">
                      Chưa lưu được — {blockingIssues.join(' · ')}
                    </p>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-5 flex items-center justify-end gap-2 border-t border-[#252a38] px-5 py-4">
                  <button
                    onClick={handleClose}
                    disabled={saving}
                    className="rounded-lg border border-[#252a38] px-4 py-2 text-[12px] font-bold text-[#94a3b8] transition-colors hover:bg-[#1e2235] disabled:opacity-50"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || drafts.length === 0}
                    className="flex items-center gap-1.5 rounded-lg bg-[#3b82f6] px-5 py-2 text-[12px] font-bold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving && <Loader2 size={14} className="animate-spin" />}
                    {saving ? 'Đang lưu...' : 'Lưu tất cả'}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
